import { Container, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { sectorAt } from '@/config/sectors';
import { formatStat } from '@/core/records';
import { t, type I18nKey } from '@/i18n';
import { ACHIEVEMENT_ROW_H, achievementDef, buildAchievementRow } from './achievement-badges';
import type { RunEndData } from './run-end-screen';
import { bodyText, chamferPanel, label } from './widgets';

/** Right column of the run-end screens (design space, 1280×720). */
export const BODY_X = 480;
export const BODY_W = 740;
const ROW_GAP = 8;
/** Achievement rows that fit above the buttons */
const MAX_ROWS = 4;

/**
 * Victory body (EPIC D3): clear time + best, what the win unlocked, pilot
 * XP, and the achievements earned during the run.
 */
export function buildVictoryBody(data: RunEndData): Container {
  const c = new Container();
  const timeHead = label(t('victory.time'), 14, 0x9aa0a6, 3);
  timeHead.position.set(BODY_X, 104);
  const time = label(formatStat('timeAlive', data.clearTime), 54, 0xffffff, 3);
  time.position.set(BODY_X, 122);
  c.addChild(timeHead, time);
  const bestText = data.newBest
    ? t('victory.newBest')
    : data.best !== null ? t('sector.best', { time: formatStat('timeAlive', data.best) }) : '';
  const best = label(bestText, 17, data.newBest ? COLORS.gemYellow : 0x9aa0a6, 2);
  best.position.set(BODY_X + time.width + 24, 150);
  c.addChild(best);
  const xp = label(t('victory.xp', { n: data.xp }), 15, COLORS.playerCream, 2);
  xp.anchor.set(1, 0);
  xp.position.set(BODY_X + BODY_W, 154);
  c.addChild(xp);

  c.addChild(unlockBanner(data));
  c.addChild(buildEarnedList(data.earned, 300));
  return c;
}

/** "SECTOR 2 UNLOCKED · ELEMENTAL CORE" / "ENDLESS MODE UNLOCKED" banner. */
function unlockBanner(data: RunEndData): Container {
  const c = new Container();
  const unlock = data.unlock;
  const h = 64;
  const g = new Graphics();
  const accent = unlock?.kind === 'sector' ? sectorAt(unlock.index).accent : COLORS.gemYellow;
  chamferPanel(g, BODY_W, h, {
    fill: 0x15121f, stroke: unlock ? accent : 0x2a2440, strokeWidth: 3, notch: 14,
  });
  g.position.set(BODY_X, 210);
  c.addChild(g);
  let head: string;
  let sub = '';
  if (unlock?.kind === 'sector') {
    head = t('victory.unlockedSector', { n: unlock.index + 1 });
    sub = t(sectorAt(unlock.index).nameKey as I18nKey);
  } else if (unlock?.kind === 'endless') {
    head = t('victory.unlockedEndless');
    sub = t('victory.campaignDone');
  } else {
    head = t('sector.cleared');
  }
  const headText = label(head, 22, unlock ? accent : COLORS.uiAccent, 3);
  headText.anchor.set(0, 0.5);
  headText.position.set(BODY_X + 22, 210 + h / 2);
  c.addChild(headText);
  if (sub) {
    const subText = label(sub, 15, 0xd5d9de, 2);
    subText.anchor.set(1, 0.5);
    subText.position.set(BODY_X + BODY_W - 22, 210 + h / 2);
    c.addChild(subText);
  }
  return c;
}

/** "ACHIEVEMENTS EARNED" + one row per new achievement (both run ends). */
export function buildEarnedList(earned: ReadonlyArray<string>, y: number): Container {
  const c = new Container();
  const head = label(t('victory.earned'), 14, 0x9aa0a6, 3);
  head.position.set(BODY_X, y);
  c.addChild(head);
  if (earned.length === 0) {
    const none = bodyText(t('victory.noneEarned'), 14, 0x6a7076);
    none.anchor.set(0, 0);
    none.position.set(BODY_X, y + 26);
    c.addChild(none);
    return c;
  }
  earned.slice(0, MAX_ROWS).forEach((id, i) => {
    const def = achievementDef(id);
    if (!def) return;
    const row = buildAchievementRow(def, true, BODY_W);
    row.position.set(BODY_X, y + 24 + i * (ACHIEVEMENT_ROW_H + ROW_GAP));
    c.addChild(row);
  });
  if (earned.length > MAX_ROWS) {
    const more = label(`+${earned.length - MAX_ROWS}`, 14, COLORS.gemYellow, 1);
    more.anchor.set(1, 0);
    more.position.set(BODY_X + BODY_W, y);
    c.addChild(more);
  }
  return c;
}
